import React, { useCallback } from "react";
import { getUserDataSelector } from "@quest-finance/quest-fe-shared/dist/auth";
import { UserData } from "@quest-finance/quest-fe-shared/dist/auth/types/UserData";
import AsyncSearchSelect, {
  AsyncSearchLoadResult,
  DROPDOWN_RESULT_LIMIT,
} from "@quest-finance/quest-fe-shared/dist/common/components/AsyncSearchSelect/AsyncSearchSelect";
import { LabelValue } from "@quest-finance/quest-fe-shared/dist/common/types/LabelValue";
import { processErrorMessage } from "@quest-finance/quest-fe-shared/dist/error-handler/utils";
import * as notification from "@quest-finance/quest-fe-shared/dist/notification";
import { AxiosError } from "axios";
import { useSelector } from "react-redux";
import UserService from "../services/UserService";

const {
  dispatch: { useNotificationDispatch },
  actionCreator: { setNotification },
} = notification;

type UserListDropdownProps = {
  name?: string;
  value: LabelValue<number> | null | undefined;
  onChange: (value: LabelValue<number> | null | undefined) => void;
  className?: string;
  placeHolder?: string;
  loadOnMount?: boolean;
  isClearable?: boolean;
  isIntroducer?: boolean;
  id?: string;
};

const UserListDropdown: React.FunctionComponent<UserListDropdownProps> = ({
  name,
  value,
  onChange,
  className,
  placeHolder,
  loadOnMount = true,
  isClearable,
  isIntroducer = false,
  id,
}: UserListDropdownProps) => {
  const userData = useSelector(getUserDataSelector);
  const notifDispatch = useNotificationDispatch();
  const onChangeCallback = useCallback(
    (value: LabelValue<number> | null | undefined) => onChange(value),
    [onChange]
  );

  const loadUsers = useCallback(
    async (
      inputValue: string
    ): Promise<AsyncSearchLoadResult<LabelValue<number>>> => {
      const { clientId } = userData as UserData;
      try {
        const result = await UserService.getUserList({
          offset: 0,
          limit: DROPDOWN_RESULT_LIMIT,
          order: "firstName",
          keywords: inputValue,
          clientIds: clientId,
          isIntroducer,
        });

        return {
          options: result.data.map((user) => ({
            label: `${user.firstName} ${user.lastName}`,
            value: user.id,
          })),
          count: result.count,
        };
      } catch (error) {
        const body = processErrorMessage(error as AxiosError);
        notifDispatch(
          setNotification({
            id: "user-list-error",
            body,
            className: "qst-notif-danger",
          })
        );
      }

      return { options: [], count: 0 };
    },
    [userData, isIntroducer, notifDispatch]
  );

  return (
    <AsyncSearchSelect
      name={name}
      isClearable={isClearable}
      className={className}
      loadOptions={loadUsers}
      value={value}
      onChange={onChangeCallback}
      placeholder={placeHolder}
      searchPlaceholder="Type to search users..."
      defaultOptions={loadOnMount}
      id={id}
    />
  );
};

export default UserListDropdown;
